import { TradingFooter, TradingHeader, TradingSidebar } from '@trading-ui/custom';
import { LogoutIcon } from '@trading-ui/icons';
import { setUserInLoggerContext } from '@trading-utils/logger';
import { useCognito } from '@trading-utils/sso-cognito';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import GasIcon from '@/assets/gas-icon';
import HomeIcon from '@/assets/home-icon';
import SettingsIcon from '@/assets/settings-icon';

import MastersLoader from '@/components/masters-loader/MastersLoader';
import ReportIssueModal from '@/components/report-issue-modal/ReportIssueModal';
import SidebarButton from '@/components/sidebar-button/SidebarButton';

import useAppContext from '@/hooks/contexts/useAppContext';
import { useWindowSize } from '@/hooks/utils/useWindowResize';

import useAppStore from '@/store/app.store';
import useConfigStore from '@/store/configStore';

import { appParameters } from '@/config/appParameters';
import { appRoutes } from '@/config/appRoutes';
import { ROLES } from '@/constants/roles';

import ReportIssueIcon from '@/assets/report-issue-icon';
import { LanguageButton } from '@/components/language-button/LanguageButton';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import type { StylesObject } from '@/interfaces/global-interfaces';
import { useSessionStorage } from 'usehooks-ts';

const getStyles = (isMobile: boolean, isUser: boolean, isHome: boolean): StylesObject => ({
  container: {
    paddingLeft: isMobile || isUser || isHome ? '1rem' : '4.5rem',
    paddingRight: '1rem',
    paddingBottom: '45px',
    paddingTop: '2rem',
    display: 'flex',
    overflow: 'hidden',
    height: 'calc(100svh - 45px)',
  },
  sidebar: {
    position: 'fixed',
    top: '3rem',
    minHeight: 'calc(100% - 8rem)',
    margin: '24px 0',
    width: '3.5rem',
    gap: '0.5rem',
  },
  sidebarIcons: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  footer: { position: 'fixed', bottom: 0 },
  icon: { width: '40px', height: '40px' },
});

export default function AppLayout() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [width] = useWindowSize();
  const { darkMode, setDarkMode, language, setLanguage } = useAppContext();
  const { logout } = useCognito();
  const { t } = useTranslation();
  const [, setHasLoggedOut] = useSessionStorage(STORAGE_KEYS.HAS_LOGGED_OUT, false);

  const user = useAppStore((state) => state.user);
  const showReportIssueModal = useAppStore((state) => state.showReportIssueModal);
  const setShowReportIssueModal = useAppStore((state) => state.setShowReportIssueModal);
  const environmentSettings = useConfigStore((state) => state.environmentSettings);

  const isMobile = useMemo(() => width < 600, [width]);

  const styles = getStyles(isMobile, user?.role === ROLES.USER, pathname === appRoutes.HOME);

  const handleLogout = useCallback(() => {
    setHasLoggedOut(true);
    logout();
  }, [logout, setHasLoggedOut]);

  const SUBMENU_HEADER_ITEMS = useMemo(
    () => [
      {
        icon: <ReportIssueIcon size={22} color="#047dba" />,
        label: t('home.reportIssue'),
        action: () => setShowReportIssueModal(true),
      },
      {
        icon: <LogoutIcon size={22} color="#047dba" />,
        label: t('home.logout'),
        action: handleLogout,
      },
    ],
    [t, handleLogout, setShowReportIssueModal]
  );

  const HEADER_BUTTONS = useMemo(
    () => [
      {
        icon: <LanguageButton language={language} />,
        action: () => setLanguage(language === 'es' ? 'en' : 'es'),
      },
    ],
    [language, setLanguage]
  );

  const SIDEBAR_ITEMS = useMemo(
    () =>
      [
        {
          Icon: HomeIcon,
          label: t('home.sidebar.home'),
          action: () => navigate(`${environmentSettings?.BASE_PATH ?? ''}${appRoutes.HOME}`),
          active: pathname === `${environmentSettings?.BASE_PATH ?? ''}${appRoutes.HOME}`,
          group: [ROLES.USER, ROLES.MOEVE_ADMIN, ROLES.MANAGER],
        },
        {
          Icon: GasIcon,
          label: t('home.sidebar.gasConsumer'),
          action: () => navigate(`${environmentSettings?.BASE_PATH ?? ''}${appRoutes.CONSUMER}`),
          active: pathname === `${environmentSettings?.BASE_PATH ?? ''}${appRoutes.CONSUMER}`,
          group: [ROLES.USER, ROLES.MOEVE_ADMIN, ROLES.MANAGER],
        },
        {
          Icon: SettingsIcon,
          label: t('home.sidebar.settings'),
          action: () => navigate(`${environmentSettings?.BASE_PATH ?? ''}${appRoutes.SETTINGS}`),
          active: pathname === `${environmentSettings?.BASE_PATH ?? ''}${appRoutes.SETTINGS}`,
          group: [ROLES.MOEVE_ADMIN, ROLES.MANAGER],
        },
      ].filter((item) => item.group.some((group) => user?.role === group)),
    [environmentSettings?.BASE_PATH, navigate, pathname, user?.role, t]
  );

  const handleLogoClick = () => navigate(appRoutes.HOME);

  if (user) {
    // @ts-ignore
    setUserInLoggerContext(user);
  }

  return (
    <>
      {showReportIssueModal && <ReportIssueModal onClose={setShowReportIssueModal} />}
      <MastersLoader />
      <TradingHeader
        buttons={HEADER_BUTTONS}
        user={{
          name: user?.name ?? '',
          image: null,
        }}
        darkMode={darkMode}
        handleDarkMode={() => setDarkMode(!darkMode)}
        appName={appParameters.APP_NAME}
        navigate={navigate}
        dropdownItems={SUBMENU_HEADER_ITEMS}
        handleLogoClick={handleLogoClick}
        mobile={isMobile}
        loadingAppsList={false}
        isExternal
      />
      {width > 600 && pathname !== appRoutes.HOME && user?.role !== ROLES.USER && (
        <TradingSidebar style={styles.sidebar}>
          <div style={styles.sidebarIcons}>
            {SIDEBAR_ITEMS.map((item) => {
              const { Icon, action, label, active } = item;

              return <SidebarButton key={label} Icon={Icon} action={action} active={active} label={label} />;
            })}
          </div>
        </TradingSidebar>
      )}
      <div style={styles.container}>
        <Outlet />
      </div>
      <TradingFooter style={styles.footer} isExternal />
    </>
  );
}
